/**
 * Agent Service - API client for AI dashboard generation
 *
 * Provides functions to interact with the /api/agent endpoints:
 * - Get available metrics
 * - Generate dashboard from business objective
 * - Apply generated configuration
 */

import axios from 'axios';
import config from '../config/config.js';

const API_BASE_URL = config.apiBaseUrl;

/**
 * Get available metrics from METRICS_MART
 *
 * @returns {Promise<Object>} Response with metrics list
 */
export const getAvailableMetrics = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/agent/metrics`);
    return response.data;
  } catch (error) {
    console.error('Error fetching available metrics:', error);
    throw error;
  }
};

/**
 * Generate dashboard configuration from business objective with conversation support
 *
 * @param {Object} request - Generation request
 * @param {string} request.objective - Business objective description
 * @param {string} request.period - Time period (Monthly, Quarterly, etc.)
 * @param {string} request.template_preference - Dashboard template type
 * @param {string} request.dashboard_name - Optional custom dashboard name
 * @param {string} request.starts_from - Start date (YYYY-MM-DD)
 * @param {Array} request.conversation_history - Previous conversation messages (optional)
 * @param {Object} request.current_dashboard_yaml - Current dashboard YAML for refinement (optional)
 * @returns {Promise<Object>} Response with generated dashboard and YAML config
 */
export const generateDashboard = async (request) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/agent/generate`, request);
    return response.data;
  } catch (error) {
    console.error('Error generating dashboard:', error);
    throw error;
  }
};

/**
 * Apply generated configuration to YAML file
 *
 * @param {Object} request - Apply request
 * @param {Object} request.yaml_config - YAML configuration to apply
 * @param {boolean} request.append_to_existing - Whether to append or replace
 * @param {string} request.dashboard_tab - Optional tab name override
 * @returns {Promise<Object>} Response with success status
 */
export const applyConfiguration = async (request) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/agent/apply`, request);
    return response.data;
  } catch (error) {
    console.error('Error applying configuration:', error);
    throw error;
  }
};

export default {
  getAvailableMetrics,
  generateDashboard,
  applyConfiguration
};
